/* Greaser's Alley — shared behaviour: header (scroll state, mobile menu, dropdowns, search),
   drawers, predictive search, scroll reveals and the motion touches from the original design
   (magnetic buttons, card spotlight, photo tilt, pinstripe ornaments), product tabs and the
   policy-page table of contents. Cart behaviour lives in cart.js, the home hero in hero.js. */
(function () {
  'use strict';
  const theme = (window.theme = window.theme || {});
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  document.documentElement.classList.remove('no-js');

  /* ---------------- drawers (menu, cart) ---------------- */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])';
  let lastTrigger = null;

  function trapFocus(e) {
    const drawer = document.querySelector('.drawer.is-open');
    if (!drawer) return;
    if (e.key === 'Escape') { closeDrawer(drawer); return; }
    if (e.key !== 'Tab') return;
    const items = [...drawer.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openDrawer(drawer, trigger) {
    if (typeof drawer === 'string') drawer = document.getElementById(drawer);
    if (!drawer) return;
    document.querySelectorAll('.drawer.is-open').forEach(d => d !== drawer && closeDrawer(d, true));
    lastTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    document.querySelectorAll('[aria-controls="' + drawer.id + '"]').forEach(b => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = drawer.querySelector('[data-drawer-focus]') || drawer.querySelector('.drawer__close');
    setTimeout(() => focusTarget && focusTarget.focus(), 50);
    document.addEventListener('keydown', trapFocus);
  }

  function closeDrawer(drawer, keepScrollLock) {
    if (typeof drawer === 'string') drawer = document.getElementById(drawer);
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!keepScrollLock) document.body.classList.remove('no-scroll');
    document.querySelectorAll('[aria-controls="' + drawer.id + '"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', trapFocus);
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  }

  theme.openDrawer = openDrawer;
  theme.closeDrawer = closeDrawer;

  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen, opener); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); closeDrawer(closer.closest('.drawer')); }
  });

  /* ---------------- desktop dropdowns ---------------- */
  function initDropdowns(root) {
    root.querySelectorAll('[data-dropdown]').forEach(li => {
      const btn = li.querySelector('button');
      let timer;
      const open = () => {
        clearTimeout(timer);
        root.querySelectorAll('[data-dropdown].is-open').forEach(o => o !== li && setOpen(o, false));
        setOpen(li, true);
      };
      const close = () => { timer = setTimeout(() => setOpen(li, false), 160); };
      btn.addEventListener('click', () => setOpen(li, !li.classList.contains('is-open')));
      if (finePointer) {
        li.addEventListener('mouseenter', open);
        li.addEventListener('mouseleave', close);
      }
      li.addEventListener('focusout', e => { if (!li.contains(e.relatedTarget)) setOpen(li, false); });
      li.addEventListener('keydown', e => { if (e.key === 'Escape') { setOpen(li, false); btn.focus(); } });
    });
  }
  function setOpen(li, open) {
    li.classList.toggle('is-open', open);
    li.querySelector('button').setAttribute('aria-expanded', open);
  }
  document.addEventListener('click', e => {
    if (!e.target.closest('[data-dropdown]')) document.querySelectorAll('[data-dropdown].is-open').forEach(li => setOpen(li, false));
  });

  /* ---------------- predictive search ---------------- */
  function initSearch(header) {
    const toggle = header.querySelector('[data-search-toggle]');
    const input = header.querySelector('[data-predictive-input]') || header.querySelector('input[name="q"]');
    if (toggle && input) {
      toggle.addEventListener('click', () => {
        const open = header.classList.toggle('search-open');
        toggle.setAttribute('aria-expanded', open);
        if (open) input.focus();
      });
    }

    const wrap = header.querySelector('[data-predictive-search]');
    const results = header.querySelector('[data-predictive-results]');
    if (!wrap || !results || !input.hasAttribute('data-predictive-input')) return;

    let controller, debounce, cache = {};
    const hide = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
    const show = () => { results.hidden = false; input.setAttribute('aria-expanded', 'true'); };

    input.addEventListener('input', () => {
      clearTimeout(debounce);
      const q = input.value.trim();
      if (q.length < 2) { hide(); return; }
      debounce = setTimeout(() => fetchResults(q), 180);
    });
    input.addEventListener('focus', () => { if (results.innerHTML.trim() && input.value.trim().length >= 2) show(); });
    input.addEventListener('keydown', e => {
      const options = [...results.querySelectorAll('[role="option"], .p-all')];
      if (!options.length || results.hidden) return;
      const i = options.findIndex(o => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = e.key === 'ArrowDown' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
        options.forEach(o => o.setAttribute('aria-selected', 'false'));
        options[next].setAttribute('aria-selected', 'true');
        options[next].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && i > -1) {
        e.preventDefault(); options[i].click();
      } else if (e.key === 'Escape') { hide(); }
    });
    document.addEventListener('click', e => { if (!wrap.contains(e.target)) hide(); });

    function fetchResults(q) {
      if (cache[q]) { render(cache[q]); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = theme.routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page,article&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag' +
        '&section_id=predictive-search';
      fetch(url, { signal: controller.signal })
        .then(r => r.ok ? r.text() : Promise.reject(r.status))
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          cache[q] = html ? html.innerHTML : '';
          render(cache[q]);
        })
        .catch(err => { if (err.name !== 'AbortError') hide(); });
    }
    function render(html) {
      results.innerHTML = html;
      html.trim() ? show() : hide();
    }
  }

  /* ---------------- header: scroll state, burger, search ---------------- */
  function initHeader(nav) {
    if (nav.dataset.ready) return;
    nav.dataset.ready = '1';
    const onScroll = () => nav.classList.toggle('scrolled', window.scrollY > 30);
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    const burger = nav.querySelector('[data-burger]');
    const setMenu = open => {
      nav.classList.toggle('open', open);
      if (!burger) return;
      burger.setAttribute('aria-expanded', open);
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    };
    if (burger) burger.addEventListener('click', () => {
      const open = !nav.classList.contains('open');
      if (open) { nav.classList.remove('search-open'); nav.querySelectorAll('[data-search-toggle]').forEach(t => t.setAttribute('aria-expanded', 'false')); }
      setMenu(open);
    });
    nav.querySelectorAll('.nav-menu a').forEach(a => a.addEventListener('click', () => setMenu(false)));
    nav.addEventListener('keydown', e => { if (e.key === 'Escape' && nav.classList.contains('open')) { setMenu(false); burger && burger.focus(); } });
    const toggle = nav.querySelector('[data-search-toggle]');
    if (toggle) toggle.addEventListener('click', () => setMenu(false));
  }

  /* ---------------- scroll reveals + pinstripe ornaments ---------------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver(entries => entries.forEach(e => {
    if (!e.isIntersecting) return;
    e.target.classList.add('in');
    if (e.target.classList.contains('ornament')) e.target.classList.add('drawn');
    io.unobserve(e.target);
  }), { threshold: 0.12, rootMargin: '0px 0px -5% 0px' }) : null;

  function initReveals(root) {
    root.querySelectorAll('.ornament path').forEach(p => { try { p.style.setProperty('--len', Math.ceil(p.getTotalLength()) + 2); } catch (e) { /* not rendered */ } });
    root.querySelectorAll('.rv:not(.in), .reveal:not(.in)').forEach(el => {
      if (!io || reduce) { el.classList.add('in'); if (el.classList.contains('ornament')) el.classList.add('drawn'); return; }
      io.observe(el);
    });
  }

  /* ---------------- magnetic buttons, card spotlight, photo tilt ---------------- */
  function initMotion(root) {
    if (!finePointer || reduce) return;
    root.querySelectorAll('.magnetic').forEach(b => {
      if (b.dataset.motion) return; b.dataset.motion = 1;
      b.addEventListener('mousemove', e => {
        const r = b.getBoundingClientRect();
        const mx = Math.max(-7, Math.min(7, (e.clientX - r.left - r.width / 2) * 0.225));
        const my = Math.max(-7, Math.min(7, (e.clientY - r.top - r.height / 2) * 0.225));
        b.style.transform = 'translate(' + mx + 'px,' + my + 'px)';
      });
      b.addEventListener('mouseleave', () => { b.style.transform = ''; });
    });
    root.querySelectorAll('.card').forEach(c => {
      if (c.dataset.motion) return; c.dataset.motion = 1;
      c.addEventListener('pointermove', e => {
        const r = c.getBoundingClientRect();
        c.style.setProperty('--cx', ((e.clientX - r.left) / r.width * 100) + '%');
        c.style.setProperty('--cy', ((e.clientY - r.top) / r.height * 100) + '%');
      });
    });
    root.querySelectorAll('[data-tilt]').forEach(tilt => {
      if (tilt.dataset.motion) return; tilt.dataset.motion = 1;
      const fig = tilt.querySelector('figure');
      if (!fig) return;
      tilt.addEventListener('mousemove', e => {
        const r = tilt.getBoundingClientRect(), x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
        fig.style.setProperty('--ry', ((x - .5) * 10) + 'deg'); fig.style.setProperty('--rx', ((.5 - y) * 8) + 'deg');
        fig.style.setProperty('--gx', (x * 100) + '%'); fig.style.setProperty('--gy', (y * 100) + '%');
      });
      tilt.addEventListener('mouseleave', () => { fig.style.setProperty('--ry', '0deg'); fig.style.setProperty('--rx', '0deg'); });
    });
  }

  /* the "On the road" print drifts against the scroll */
  const roadArts = new Set();
  function roadScroll() {
    roadArts.forEach(img => {
      if (!document.contains(img)) { roadArts.delete(img); return; }
      const r = img.parentNode.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0) img.style.setProperty('--sy', ((r.top + r.height / 2 - innerHeight / 2) * -0.08) + 'px');
    });
  }
  if (!reduce) addEventListener('scroll', roadScroll, { passive: true });
  function initRoad(root) { root.querySelectorAll('[data-road-art]').forEach(img => roadArts.add(img)); roadScroll(); }

  /* ---------------- product tabs ---------------- */
  function initTabs(root) {
    root.querySelectorAll('[data-tabs]').forEach(list => {
      if (list.dataset.ready) return; list.dataset.ready = '1';
      const tabs = [...list.querySelectorAll('[role=tab]')], pill = list.querySelector('.pill');
      const current = () => tabs.find(t => t.getAttribute('aria-selected') === 'true') || tabs[0];
      const place = t => { if (pill && t) { pill.style.width = t.offsetWidth + 'px'; pill.style.transform = 'translateX(' + t.offsetLeft + 'px)'; } };
      const select = t => {
        tabs.forEach(o => {
          const on = o === t;
          o.setAttribute('aria-selected', on); o.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(o.getAttribute('aria-controls'));
          if (panel) { panel.hidden = !on; if (on) initReveals(panel); }
        });
        place(t);
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', e => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]; select(n); n.focus();
        });
      });
      place(current());
      addEventListener('load', () => place(current()));
      addEventListener('resize', () => place(current()));
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => place(current()));
    });
  }

  /* ---------------- policy pages: list the H2s in the sidebar ---------------- */
  function initToc(root) {
    root.querySelectorAll('[data-toc]').forEach(list => {
      if (list.dataset.ready) return; list.dataset.ready = '1';
      const scope = list.closest('[data-toc-root]');
      const heads = scope ? [...scope.querySelectorAll('.prose h2')] : [];
      if (heads.length < 2) { list.closest('aside').hidden = true; scope && scope.classList.remove('policy-grid'); return; }
      const used = {};
      heads.forEach(h => {
        if (!h.id) {
          let id = (h.textContent || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
          used[id] = (used[id] || 0) + 1;
          if (used[id] > 1) id += '-' + used[id];
          h.id = id;
        }
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + h.id; a.textContent = h.textContent.trim();
        li.appendChild(a); list.appendChild(li);
      });
      list.hidden = false;
    });
  }

  /* ---------------- money formatting (used by cart.js / product.js) ---------------- */
  theme.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || theme.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmtNum = (n, precision, thousands, decimal) => {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(n) || n == null) return '0';
      const parts = (n / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    };
    const m = format.match(placeholder);
    if (!m) return format;
    let value;
    switch (m[1]) {
      case 'amount_no_decimals': value = fmtNum(cents, 0); break;
      case 'amount_with_comma_separator': value = fmtNum(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmtNum(cents, 0, '.', ','); break;
      default: value = fmtNum(cents, 2);
    }
    return format.replace(placeholder, value);
  };

  /* ---------------- boot + theme editor hooks ---------------- */
  theme.init = function (root) {
    root = root || document;
    initReveals(root);
    initMotion(root);
    initRoad(root);
    initTabs(root);
    initToc(root);
    root.querySelectorAll('[data-desktop-nav]').forEach(initDropdowns);
    const header = root.querySelector('[data-header]');
    if (header) { initHeader(header); initSearch(header); }
    document.dispatchEvent(new CustomEvent('theme:init', { detail: { root } }));
  };

  theme.init(document);
  document.addEventListener('shopify:section:load', e => theme.init(e.target));
  document.addEventListener('shopify:section:select', e => e.target.querySelectorAll('.rv, .reveal').forEach(el => { el.classList.add('in'); if (el.classList.contains('ornament')) el.classList.add('drawn'); }));
})();
